<template>
  <survey-question-card>
    <v-overlay
      v-if="question.willBeDeleted"
      absolute color="white"
      opacity="0.7"
      class="warning--text text-center"
    >
      <div class="mb-2">{{ trans('survey.question.willBeDeleted') }}</div>
      <v-btn @click="notifyDelete" color="warning" small><v-icon left small>mdi-undo</v-icon>Rückgängig</v-btn>
    </v-overlay>
    <slot name="drag-handle-question"></slot>
    <survey-question-number :number="number" v-if="number" class="mt-1" />
    <div class="flex-grow-1">
      <v-textarea
        v-model.lazy="question.text"
        :label="transc('survey.question.question', 1)"
        autocomplete="off"
        outlined
        height="100px"
        no-resize
        class="pt-0 mt-0"
        ref="questionTextInput"
        :hide-details="!(validationErrors && validationErrors.text || false)"
        :error-messages="validationErrors && validationErrors.text ? validationErrors.text : null"
      />
      <div v-if="questionAnswers" style="max-width: 500px;">
        <v-list>
          <draggable
            v-model="question.surveyAnswers"
            :group="'question-' + number"
            handle=".drag-handle"
            animation="100"
          >
            <v-list-item
              v-for="(answer, i) in questionAnswers"
              :key="'question-answer-' + i"
              class="question-answer-item pl-0"
            >
              <v-list-item-icon class="mr-4">
                <drag-handle v-if="typeSupportsEditingAnswers" />
                <v-icon>{{ questionTypeAnswerIcon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <v-text-field
                    v-model.lazy="answer.text"
                    autocomplete="off"
                    class="pt-0 mt-0"
                    ref="questionAnswerTextInput"
                    hide-details
                    @keypress.enter="addSurveyAnswer()"
                    @focus="onFocusTextInput"
                  />

                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="question.surveyAnswers.length > 1 && typeSupportsEditingAnswers">
                <div class="d-flex align-center">
                  <v-text-field
                    v-if="question.rankable"
                    v-model="answer.rankingPoints"
                    label="Punkte"
                    outlined
                    dense
                    hide-details
                    style="max-width: 80px;"
                  />
                  <v-btn @click="deleteSurveyAnswer(answer)" icon :title="trans('survey.question.deleteAnswer')">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </draggable>
        </v-list>
        <v-btn
          v-if="typeSupportsEditingAnswers"
          @click="addSurveyAnswer()"
          elevation="0"
        >
          <v-icon>mdi-plus</v-icon>
          {{ trans('survey.question.addAnswer') }}
        </v-btn>
      </div>
    </div>
    <div class="d-flex ml-8 flex-column">
      <v-select
        :items="types"
        :value="question.type"
        class="ml-1 pt-0 mt-0"
        style="max-width: 200px;"
        :prepend-inner-icon="currentType ? currentType.icon : null"
        @input="onTypeChange"
      >
        <template #item="{item}">
          <v-icon left>{{ item.icon }}</v-icon>{{ item.text }}
        </template>
      </v-select>

      <div class="flex-grow-1">
        <div class="d-flex justify-space-between align-center">
          <v-checkbox
            v-model="question.required"
            :label="trans('survey.question.required')"
            class="mt-0 pt-0"
            hide-details
          ></v-checkbox>
          <v-divider vertical class="mx-5"></v-divider>
          <v-btn @click="notifyDelete" icon :title="trans('survey.question.delete')">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
        <v-checkbox
          v-if="isRadioType()"
          v-model="question.rankable"
          class="mt-1"
          hide-details
          @change="onIsRankableChange"
        >
          <template #label>
            {{ trans('survey.question.rankable') }}
            <v-tooltip bottom transition="slide-y-transition" max-width="350">
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" size="18" class="ml-2" color="primary" style="cursor: help;">mdi-help-circle</v-icon>
              </template>
              <span>{{ trans('survey.question.rankableHelp') }}</span>
            </v-tooltip>
          </template>
        </v-checkbox>
      </div>

    </div>
  </survey-question-card>
</template>

<script>
import {LocaleUtils} from '../../utils/locale-utils';
import {SurveyQuestionTypes} from '@/lib/survey-question-types';
import draggable from 'vuedraggable'
import DragHandle from '@/components/DragHandle';
import SurveyQuestionCard from '@/components/survey/SurveyQuestionCard';
import SurveyQuestionNumber from '@/components/survey/SurveyQuestionNumber';
import {SurveyQuestionMixin} from '@/components/survey/SurveyQuestionMixin';

export default {
  name: 'SurveyQuestion',
  mixins: [
    LocaleUtils,
    SurveyQuestionMixin
  ],
  components: {
    SurveyQuestionNumber,
    SurveyQuestionCard,
    DragHandle,
    draggable
  },
  props: {
    number: {
      type: Number,
    },
    question: {
      type: Object,
      required: true
    },
    validationErrors: {
      default: null
    }
  },
  computed: {
    types () {
      return [
        {
          value: SurveyQuestionTypes.MULTIPLE_CHOICE_SINGLE,
          text: this.trans('survey.question.typeMultipleChoiceSingle'),
          icon: 'mdi-radiobox-marked'
        },
        {
          value: SurveyQuestionTypes.MULTIPLE_CHOICE,
          text: this.trans('survey.question.typeMultipleChoice'),
          icon: 'mdi-checkbox-marked-outline'
        },
        {
          value: SurveyQuestionTypes.TEXT,
          text: this.trans('survey.question.typeText'),
          icon: 'mdi-text'
        },
        // Templates / Helpers
        {
          header: this.trans('survey.question.typeTemplates')
        },
        {
          value: SurveyQuestionTypes.YES_NO,
          text: this.trans('survey.question.typeYesNo'),
          icon: 'mdi-call-split'
        }
      ];
    },
    currentType () {
      let currentType = null;

      this.types.forEach(type => {
        if (type.value === this.question.type) {
          currentType = type;
        }
      });

      return currentType;
    },
    questionTypeAnswerIcon () {
      let questionTypeAnswerIcon = null;

      switch (this.question.type) {
        case SurveyQuestionTypes.MULTIPLE_CHOICE_SINGLE:
        case SurveyQuestionTypes.YES_NO:
          questionTypeAnswerIcon = 'mdi-radiobox-blank';
          break;
        case SurveyQuestionTypes.MULTIPLE_CHOICE:
          questionTypeAnswerIcon = 'mdi-checkbox-blank-outline';
          break;
      }

      return questionTypeAnswerIcon;
    },
    typeSupportsEditingAnswers () {
      return this.isType(SurveyQuestionTypes.MULTIPLE_CHOICE_SINGLE, SurveyQuestionTypes.MULTIPLE_CHOICE, SurveyQuestionTypes.YES_NO);
    }
  },
  methods: {
    addSurveyAnswer (...preDefinedAnswer) {
      const visibleAnswerCount = this.questionAnswers.length;
      if (typeof preDefinedAnswer !== 'undefined' && preDefinedAnswer.length > 0) {
        this.question.surveyAnswers.push(...preDefinedAnswer);
      } else {
        this.question.surveyAnswers.push({
          text: 'Option ' + (this.questionAnswers?.length + 1 || 1)
        });
      }
      this.$nextTick(() => {
        const questionAnswerTextInputElement = this.$refs.questionAnswerTextInput[visibleAnswerCount];
        try {
          questionAnswerTextInputElement.focus();
          questionAnswerTextInputElement.$refs.input.select();
        } catch (e) {
          console.warn(e);
        }
      });
    },
    deleteSurveyAnswer (answer) {
      let index = this.question.surveyAnswers.indexOf(answer);
      if (index >= 0) {
        this.question.surveyAnswers.splice(index, 1);
      }
    },
    onFocusTextInput (event) {
      try {
        event.target.select();
      } catch (e) {
        console.warn(e);
      }
    },
    onTypeChange (type) {

      switch (type) {
        case SurveyQuestionTypes.TEXT:
          this.$set(this.question, 'surveyAnswers', []);
          break;

        case SurveyQuestionTypes.YES_NO:
          this.$set(this.question, 'surveyAnswers', []);
          this.addSurveyAnswer(
            { text: this.trans('general.yes') },
            { text: this.trans('general.no') }
          );
          type = SurveyQuestionTypes.MULTIPLE_CHOICE_SINGLE;
          break;
      }

      if (type !== SurveyQuestionTypes.TEXT && this.question.surveyAnswers.length === 0) {
        this.addSurveyAnswer();
      }

      this.question.type = type;

      if (!this.isRadioType()) {
        this.question.rankable = false;
      }
    },

    onIsRankableChange (rankable) {
      if (rankable) {
        this.question.surveyAnswers?.forEach((answer, i) => {
          if (!answer.rankingPoints) {
            answer.rankingPoints = this.question.surveyAnswers.length - i - 1;
          }
        });
      }
    },

    notifyDelete () {
      this.$emit('delete');
    }
  },
  watch: {
    question: {
      immediate: true,
      deep: true,
      handler (before, after) {
        this.question.surveyAnswers?.forEach((answer, i) => answer.order = i);
      }
    }
  },
  mounted () {
    if (!this.question.text && this.$refs.questionTextInput) {
      this.$refs.questionTextInput.focus();
    }
  }
}
</script>

<style scoped lang="scss">
.drag-handle {
  opacity: 0.3;
}

.question-answer-item:hover {
  .drag-handle {
    opacity: 1;
  }
}
</style>
